import { supabase } from "../supabase/SupabaseClient";
import { normalizarNumero } from "../shared/choferes";

export { normalizarNumero };

/* "Choferes autorizados" — el sector de Satelital (P-187 whitelist).
 *
 * ── SUPABASE ES EL PRIMARIO, EL SHEET ES EL CLON (RAID) ─────────────────────
 *
 * Lo que se carga acá habilita al chofer a escribirle al bot al toque. El bot
 * lee esta tabla (whitelist_usuarios), y aparte se clona al Sheet como copia.
 *
 * ── LO QUE SE VE VIENE DE RRHH, PERO SATELITAL NO TIENE ver.rrhh ────────────
 *
 * Nombre, legajo y empresa viven en la nómina, que Satelital no puede leer. Por
 * eso el listado y el autocompletado van por funciones `security definer`
 * (`choferes_lista`, `choferes_buscar_nomina`) que chequean `ver.choferes` y
 * traen sólo lo de los choferes — nunca la nómina entera. El candado lo pone la
 * base: sin permiso, las RPC vuelven vacías y las tablas rebotan la escritura.
 */

/* El listado con nombre/legajo/empresa vivos de RRHH y el tractor asignado. */
export async function listaChoferes() {
  const { data, error } = await supabase.rpc("choferes_lista");
  if (error) throw error;
  return data || [];
}

/* Autocompletar por nombre desde la nómina. `ya` marca al que ya está en la
 * lista, para no duplicarlo. */
export async function buscarNomina(q) {
  const t = String(q ?? "").trim();
  if (t.length < 2) return [];
  const { data, error } = await supabase.rpc("choferes_buscar_nomina", { q: t });
  if (error) throw error;
  return data || [];
}

/* Alta: sólo el DNI y el número. Nombre/legajo/empresa/rol/fecha los pone la
 * base (el trigger valida que el DNI esté en RRHH y sella quién y cuándo). */
export async function altaChofer(dni, numero) {
  const { error } = await supabase
    .from("choferes_autorizados")
    .insert({ dni: String(dni), numero: normalizarNumero(numero) || null });
  if (error) throw error;
}

export async function editarNumero(dni, numero) {
  const { error } = await supabase
    .from("choferes_autorizados")
    .update({ numero: normalizarNumero(numero) || null })
    .eq("dni", String(dni));
  if (error) throw error;
}

/* Baja no borra: deja de estar autorizado pero queda con quién y cuándo. */
export async function setActivo(dni, activo) {
  const { error } = await supabase
    .from("choferes_autorizados")
    .update({ activo: !!activo })
    .eq("dni", String(dni));
  if (error) throw error;
}

/* El tractor asignado (interno o patente) → chofer_tractor, con su historial.
 * Va por RPC porque escribir chofer_tractor pide `cargar.flota`, que Satelital
 * no tiene: la función lo hace con permiso propio chequeando `cargar.choferes`. */
export async function setTractor(dni, ref) {
  const { error } = await supabase.rpc("choferes_set_tractor", {
    p_dni: String(dni),
    p_ref: String(ref ?? ""),
  });
  if (error) throw error;
}

/* La bienvenida no salió (número malo, Meta caída): la vuelve a encolar y el
 * bot la reintenta en el próximo barrido. El botón aparece sólo cuando falló. */
export async function reencolarBienvenida(dni) {
  const { error } = await supabase.rpc("choferes_bienvenida_reencolar", {
    p_dni: String(dni),
  });
  if (error) throw error;
}

/* Encola una bienvenida de prueba a un número suelto (sale aunque la flag del
 * bot esté apagada — es un test explícito). NO se muestra en la UI de PROD (a
 * pedido de Igna): queda como capacidad para UAT / uso manual. La RPC sigue
 * viva y gateada por `cargar.choferes`. */
export async function probarBienvenida(numero) {
  const { data, error } = await supabase.rpc("choferes_bienvenida_probar", {
    p_numero: normalizarNumero(numero) || String(numero || "").trim(),
  });
  if (error) throw error;
  return data;
}

/* Cuando quieren dar de alta a alguien que NO está en RRHH: en vez de dejarlo
 * cargar un nombre suelto, se abre un ticket para que RRHH lo sume.
 *
 * ── VA POR `abrir_ticket`, NO POR `abrir_ticket_bot` ────────────────────────
 *
 * `abrir_ticket_bot` está revocada para `authenticated` a propósito —«Sólo
 * service_role (la llama FlotaBot)», dice su migración—, así que desde el
 * navegador SIEMPRE devolvía `42501 permission denied` y el botón no abrió un
 * solo ticket desde que existe. Lautaro Díaz (Satelital), 14/09/2026: *«en
 * choferes autorizados quiero buscar un chofer que ingresó hace poco y no
 * aparece. Clickeo en crear ticket pero me tira eso: No pude abrir el
 * ticket»*.
 *
 * Y no se arregla dándole el permiso: esa función deja elegir de parte de
 * QUIÉN viene el ticket, y repartirla al navegador es dejar abrir tickets a
 * nombre de cualquiera. `abrir_ticket` saca el autor de la sesión
 * (`mi_mail()`), que es lo que corresponde cuando hay alguien logueado. */
export async function ticketFaltaPersona(nombre) {
  const quien = String(nombre ?? "").trim();
  const { data, error } = await supabase.rpc("abrir_ticket", {
    p_titulo: `Falta en RRHH para autorizar como chofer: ${quien.slice(0, 90)}`,
    p_area: "rrhh",
    p_detalle:
      `Satelital quiso autorizar a «${quien}» como chofer del bot pero no ` +
      `aparece en la nómina de RRHH. Hay que darlo de alta en RRHH para poder ` +
      `sumarlo a "Choferes autorizados".`,
    p_prioridad: "normal",
    p_sobre: quien || null,
  });
  if (error) throw error;
  return data;
}
